import { escapeHtml } from '../../utils/format';

// Labels printed per work order unless the operator changes it: four of the large
// labels, a full sheet of the small ones.
export const DEFAULT_LABEL_COPIES = { '1': 4, '4': 4, '21': 21 };

// Build the printable HTML document for production labels.
// layout: '1' (full page), '4' (99.1 x 139mm, 2x2 on A4) or '21' (Avery L7160, 3x7).
// Each label shows the report's AL number from `lineResults` when available.
// options.copies: labels per work order (default DEFAULT_LABEL_COPIES[layout]).
// options.date: the date printed on the label - the report's date for reprints.
export const buildLabelsDocument = (items, lineResults, layout, options = {}) => {
  const copies = Math.max(1, Number(options.copies) || DEFAULT_LABEL_COPIES[layout] || 1);
  const labelDate = new Date(options.date || Date.now());
  const dateText = (isNaN(labelDate.getTime()) ? new Date() : labelDate).toLocaleDateString('en-GB');
  const copiesOf = (html) => Array(copies).fill(html);

  const buildLabelHtml = (item, sizeClass) => {
    const lr = lineResults?.find(r => r.serialName === item.serialName);
    const al = lr?.al || item.al || '-';
    return `
      <div class="label ${sizeClass}">
        <div class="label-header">Production Report</div>
        <div class="label-report-no">${escapeHtml(al)}</div>
        <div class="label-field"><span class="label-key">WO:</span> <span class="label-val">${escapeHtml(item.serialName || '-')}</span></div>
        <div class="label-field"><span class="label-key">Part:</span> <span class="label-val">${escapeHtml(item.partDescription || item.partName || '-')}</span></div>
        <div class="label-field"><span class="label-key">Qty:</span> <span class="label-val">${escapeHtml(item.quantity || '-')}</span></div>
        <div class="label-date">${escapeHtml(dateText)}</div>
      </div>
    `;
  };

  let labelsHtml = '';
  let layoutCss = '';

  if (layout === '1') {
    // 1 per page - each copy on its own page
    labelsHtml = items.flatMap((item) => copiesOf(buildLabelHtml(item, 'full'))).join('');
    layoutCss = `
      .label.full {
        width: 100%; height: 100vh;
        page-break-after: always;
        padding: 15mm;
        justify-content: center; gap: 6mm;
      }
      .label.full .label-header { font-size: 14pt; margin-bottom: 4mm; }
      .label.full .label-report-no { font-size: 48pt; margin-bottom: 6mm; }
      .label.full .label-field { font-size: 14pt; line-height: 1.5; }
      .label.full .label-date { font-size: 10pt; }
    `;
  } else if (layout === '4') {
    // 4 per page (2x2 grid, 99.1 x 139mm)
    const labelsPerPage = 4;
    const allLabels = items.flatMap((item) => copiesOf(buildLabelHtml(item, 'quad')));
    // Group into pages of 4
    for (let i = 0; i < allLabels.length; i += labelsPerPage) {
      const pageLabels = allLabels.slice(i, i + labelsPerPage);
      // Pad to 4 if needed
      while (pageLabels.length < labelsPerPage) pageLabels.push('<div class="label quad empty"></div>');
      labelsHtml += `<div class="label-page${i > 0 ? ' page-break' : ''}">${pageLabels.join('')}</div>`;
    }
    layoutCss = `
      .label-page {
        width: 210mm; height: 297mm;
        display: grid; grid-template-columns: 1fr 1fr; grid-template-rows: 1fr 1fr;
        padding: 9.5mm 6.4mm; gap: 0;
      }
      .page-break { page-break-before: always; }
      .label.quad {
        width: 99.1mm; height: 139mm; padding: 10mm 8mm;
        justify-content: center; gap: 4mm;
      }
      .label.quad .label-header { font-size: 11pt; margin-bottom: 2mm; }
      .label.quad .label-report-no { font-size: 36pt; margin-bottom: 4mm; }
      .label.quad .label-field { font-size: 11pt; line-height: 1.4; }
      .label.quad .label-date { font-size: 9pt; }
    `;
  } else if (layout === '21') {
    // 21 per page (3x7 grid, Avery L7160: 63.5 x 38.1mm)
    const labelsPerPage = 21;
    const allLabels = items.flatMap((item) => copiesOf(buildLabelHtml(item, 'small')));
    for (let i = 0; i < allLabels.length; i += labelsPerPage) {
      const pageLabels = allLabels.slice(i, i + labelsPerPage);
      while (pageLabels.length < labelsPerPage) pageLabels.push('<div class="label small empty"></div>');
      labelsHtml += `<div class="label-page${i > 0 ? ' page-break' : ''}">${pageLabels.join('')}</div>`;
    }
    layoutCss = `
      .label-page {
        width: 210mm; height: 297mm;
        display: grid; grid-template-columns: repeat(3, 64mm); grid-template-rows: repeat(7, 38mm);
        padding: 15mm 8mm; column-gap: 3mm; row-gap: 0;
      }
      .page-break { page-break-before: always; }
      .label.small {
        width: 64mm; height: 38mm; padding: 2mm 3mm;
        justify-content: center; gap: 1mm;
      }
      .label.small .label-header { font-size: 6pt; margin-bottom: 0; }
      .label.small .label-report-no { font-size: 16pt; margin-bottom: 1mm; }
      .label.small .label-field { font-size: 7pt; line-height: 1.2; }
      .label.small .label-date { font-size: 6pt; }
    `;
  }

  return `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Production Labels</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          @page { size: A4; margin: 0; }
          body { font-family: Arial, sans-serif; }
          .label {
            display: flex; flex-direction: column;
          }
          .label-header {
            color: #666; text-transform: uppercase;
            letter-spacing: 1px; text-align: center;
          }
          .label-report-no {
            font-weight: bold; text-align: center; color: #111;
          }
          .label-key { font-weight: bold; color: #333; }
          .label-val { color: #111; }
          .label-date {
            color: #999; text-align: right; margin-top: auto;
          }
          .label.empty { visibility: hidden; }
          ${layoutCss}
        </style>
      </head>
      <body>${labelsHtml}</body>
      </html>
    `;
};
